import React, { useContext } from 'react'
import { DateTime } from 'luxon'
import ContentNodeContext from '../contexts/content-node'
import Link from './link'
import Logos from './logos'
import Container from '../components/container'
import Grid from '../components/grid'

export default function CtaEvents({ content, sponsors }) {
  const contentNode = useContext(ContentNodeContext)
  const { heading, link, events } = content ?? {}
  const { logos } = sponsors ?? {}
  let items

  if (events?.length) {
    items = events.map(({ location, dates }) => ({
      location: location.title,
      dates
    }))
  } else {
    const {
      contentTypeName,
      acf: { meta }
    } = contentNode

    const type = ['seminar', 'show'].includes(contentTypeName)
      ? 'program'
      : contentTypeName
    const { locations } = meta[type] ?? {}

    items =
      locations
        ?.sort((a, b) => {
          const { dates: aDates } = a?.acf?.meta?.location ?? {}
          const { dates: bDates } = b?.acf?.meta?.location ?? {}

          if (!aDates || !bDates) {
            return 0
          }

          const [aStart] = aDates
          const [bStart] = bDates

          if (aStart < bStart) {
            return -1
          }

          if (aStart > bStart) {
            return 1
          }

          return 0
        })
        ?.map(({ title, acf }) => {
          const { dates } = acf?.meta?.location ?? {}

          if (!dates) {
            return { title }
          }

          const [start, end] = dates.map((date) => DateTime.fromISO(date))
          let dateString

          if (start.year !== end.year) {
            dateString = `${start.toFormat('LLL d, y')} – ${end.toFormat(
              'LLL d, y'
            )}`
          } else if (start.month !== end.month) {
            dateString = `${start.toFormat('LLL d')} – ${end.toFormat(
              'LLL d, y'
            )}`
          } else if (start.day !== end.day) {
            dateString = `${start.toFormat('LLL d')}–${end.toFormat('d, y')}`
          } else {
            dateString = start.toFormat('LLL d, y')
          }

          return { location: title, dates: dateString }
        }) ?? []
  }

  return (
    <section className="bg-secondary text-primary">
      <Container>
        <Grid className="border-tertiary items-center border-b-2 py-8">
          <div className="col-span-12 md:col-span-10 md:col-start-2 lg:col-span-6 lg:col-start-1 xl:col-span-5">
            {heading && <h2 className="heading-lg-display">{heading}</h2>}
            {items && (
              <ul className="accent-lg mt-4 w-full">
                {items.map(({ location, dates }, key) => (
                  <li className="mt-2 grid w-full grid-cols-2" key={key}>
                    {location && (
                      <address className="not-italic">{location}</address>
                    )}
                    {dates && <div>{dates}</div>}
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="col-span-12 md:col-span-10 md:col-start-2 lg:col-span-6 lg:justify-self-end xl:col-span-7">
            {link && (
              <Link
                className="dark-link group mt-8 inline-flex h-[134px] w-[255px] flex-col items-center justify-center rounded-[50%] border-2 md:h-[200px] md:w-[420px]"
                {...link}>
                <span className="control-2xl inline-block">{link?.title}</span>
                <span className="control-3xl inline-block transition duration-150 ease-[cubic-bezier(.47,.27,.28,.86)] group-hover:translate-x-3">
                  <svg viewBox="0 0 63.8 49.4" className="mt-2 w-8 lg:w-11">
                    <path d="m26.2 42.5 32.3-16.8L0 29.1v-8.8l58.3 3.3L26.2 6.9 30.6 0l33.2 24.7-33.2 24.7-4.4-6.9Z" />
                  </svg>
                </span>
              </Link>
            )}
          </div>
        </Grid>
        <div className="py-8">{logos && <Logos size="lg" logos={logos} />}</div>
      </Container>
    </section>
  )
}
